// FinalAccountSection.js
// ─────────────────────────────────────────────────────────────────────────────
// FINAL ACCOUNT / CLOSE-OUT  (GCC 2015)
//
// The build-up of the agreed final contract value, and the close-out reconciliation
// against what has actually been certified.
//
//   Final contract value =
//       Original contract sum
//     + net variations (additions − omissions)
//     + contract price adjustment (escalation)
//     + dayworks
//     ± provisional-sum adjustments
//     − penalties / damages
//     + any other agreed adjustments
//
//   Close-out:
//       Final value − total certified to date = balance to certify on the final
//       payment certificate. Retention release is tracked alongside (the two GCC
//       6.10.3 milestones) so close-out shows what is still owed.
//
// Each build-up line is individually flagged "agreed" so you can see how much of
// the final account is settled vs still under negotiation.
//
// Firestore: projects/{projectId}/finalAccount/buildup — single doc
//   { lineItems:[{id,label,category,amount,agreed,notes}], totalCertified,
//     retentionHeld, retentionReleased, status }
//
// Self-contained: reads/writes its own doc, takes only the project.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useEffect, useMemo } from "react";
import {
  doc, setDoc, updateDoc, getDoc, onSnapshot, serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase";
import { tokens } from "./theme";

const C = tokens.color;
const MONO = tokens.font?.mono || '"JetBrains Mono", monospace';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const numberOrZero = (v) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };
const fmtR = (v) => "R " + numberOrZero(v).toLocaleString("en-ZA", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const uid = () => `li_${Math.random().toString(36).slice(2, 9)}`;

const CATEGORIES = [
  "Original Contract Sum", "Variations (net)", "Contract Price Adjustment",
  "Dayworks", "Provisional Sum Adjustment", "Penalties / Damages", "Other",
];
// Categories that conventionally subtract from the account.
const DEDUCTIONS = new Set(["Penalties / Damages"]);

const STATUSES = ["Draft", "Submitted", "Under Negotiation", "Agreed", "Certified"];
const STATUS_META = {
  "Draft":             { bg: C.neutralSoft, text: C.neutral, border: C.neutralBorder },
  "Submitted":         { bg: C.infoSoft,    text: C.info,    border: C.infoBorder },
  "Under Negotiation": { bg: C.warningSoft, text: C.warning, border: C.warningBorder },
  "Agreed":            { bg: C.successSoft, text: C.success, border: C.successBorder },
  "Certified":         { bg: C.successSoft, text: C.success, border: C.successBorder },
};

// Signed contribution of a line to the final value.
function lineSigned(li) {
  const amt = numberOrZero(li.amount);
  return DEDUCTIONS.has(li.category) ? -Math.abs(amt) : amt;
}

// ─── Shared styles ───────────────────────────────────────────────────────────

const s = {
  page: { display: "grid", gap: 20, minWidth: 0, fontFamily: tokens.font?.body || '"Inter", sans-serif', color: C.ink },
  card: { background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: 20, boxShadow: tokens.shadow?.sm, minWidth: 0 },
  title: { margin: 0, fontSize: 24, fontFamily: tokens.font?.display, color: C.ink, letterSpacing: "-0.02em" },
  sub: { margin: "4px 0 0", fontSize: 12.5, color: C.inkMuted },
  h3: { margin: "0 0 12px", fontSize: 13, fontWeight: 800, color: C.ink, letterSpacing: "-0.01em" },
  lbl: { display: "block", fontSize: 10.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: C.inkMuted, marginBottom: 4 },
  inp: { width: "100%", boxSizing: "border-box", padding: "7px 9px", borderRadius: 8, border: `1px solid ${C.border}`, background: C.surface, fontSize: 12.5, color: C.ink, fontFamily: "inherit", outline: "none" },
  btn: { padding: "8px 14px", borderRadius: 8, border: `1px solid ${C.border}`, background: C.surface, color: C.ink, fontSize: 12.5, fontWeight: 700, cursor: "pointer" },
  btnAccent: { padding: "8px 14px", borderRadius: 8, border: "none", background: C.accent, color: "#fff", fontSize: 12.5, fontWeight: 700, cursor: "pointer" },
  btnSmall: { padding: "5px 10px", borderRadius: 7, border: `1px solid ${C.border}`, background: C.surface, color: C.ink, fontSize: 11.5, fontWeight: 700, cursor: "pointer" },
  btnDanger: { padding: "5px 10px", borderRadius: 7, border: `1px solid ${C.dangerBorder}`, background: C.dangerSoft, color: C.danger, fontSize: 11.5, fontWeight: 700, cursor: "pointer" },
  th: { padding: "8px 10px", textAlign: "left", fontSize: 10.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: C.inkMuted, borderBottom: `2px solid ${C.border}`, background: C.surfaceAlt, whiteSpace: "nowrap" },
  thR: { padding: "8px 10px", textAlign: "right", fontSize: 10.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: C.inkMuted, borderBottom: `2px solid ${C.border}`, background: C.surfaceAlt, whiteSpace: "nowrap" },
  td: { padding: "7px 10px", fontSize: 12.5, borderBottom: `1px solid ${C.border}`, verticalAlign: "middle" },
  tdR: { padding: "7px 10px", fontSize: 12.5, borderBottom: `1px solid ${C.border}`, textAlign: "right", fontFamily: MONO, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" },
  chip: (m) => ({ display: "inline-block", padding: "3px 10px", borderRadius: 999, fontSize: 10, fontWeight: 800, letterSpacing: "0.03em", background: m.bg, color: m.text, border: `1px solid ${m.border}`, whiteSpace: "nowrap" }),
  mono: { fontFamily: MONO, fontVariantNumeric: "tabular-nums" },
  statTile: { background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 10, padding: "12px 14px", minWidth: 0 },
  statLabel: { fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: C.inkMuted, margin: 0 },
  statValue: { fontSize: 18, fontWeight: 800, fontFamily: MONO, color: C.ink, margin: "2px 0 0" },
};

const blankDoc = () => ({
  lineItems: [], totalCertified: "", retentionHeld: "", retentionReleased: "", status: "Draft",
});

// ═════════════════════════════════════════════════════════════════════════════
// MAIN
// ═════════════════════════════════════════════════════════════════════════════
function FinalAccountSection({ currentProject, currentProjectId }) {
  const projectId = currentProject?.id || currentProjectId;

  const [data, setData] = useState(blankDoc);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (!projectId) { setData(blankDoc()); return undefined; }
    const ref = doc(db, "projects", projectId, "finalAccount", "buildup");
    const unsub = onSnapshot(ref,
      (snap) => {
        if (snap.exists()) setData({ ...blankDoc(), ...snap.data() });
        else setData(blankDoc());
        setDirty(false);
      },
      (err) => console.error("finalAccount listener:", err)
    );
    return unsub;
  }, [projectId]);

  const lineItems = useMemo(
    () => (Array.isArray(data.lineItems) ? data.lineItems : []),
    [data.lineItems]
  );

  const finalValue = useMemo(() => lineItems.reduce((sum, li) => sum + lineSigned(li), 0), [lineItems]);
  const agreedValue = useMemo(
    () => lineItems.filter((li) => li.agreed).reduce((sum, li) => sum + lineSigned(li), 0),
    [lineItems]
  );
  const certified = numberOrZero(data.totalCertified);
  const balanceToCertify = finalValue - certified;
  const retentionHeld = numberOrZero(data.retentionHeld);
  const retentionReleased = numberOrZero(data.retentionReleased);
  const retentionOutstanding = retentionHeld - retentionReleased;
  const agreedPct = finalValue ? Math.round((agreedValue / finalValue) * 100) : 0;

  function patch(next) { setData((d) => ({ ...d, ...next })); setDirty(true); }
  function setLines(updater) {
    setData((d) => ({ ...d, lineItems: updater(Array.isArray(d.lineItems) ? d.lineItems : []) }));
    setDirty(true);
  }

  function addLine(category) {
    setLines((ls) => [...ls, {
      id: uid(),
      label: category === "Original Contract Sum" ? "Original contract sum" : "",
      category, amount: "", agreed: false, notes: "",
    }]);
  }
  const setLine = (id, p) => setLines((ls) => ls.map((li) => (li.id === id ? { ...li, ...p } : li)));
  const removeLine = (id) => setLines((ls) => ls.filter((li) => li.id !== id));

  async function save() {
    if (!projectId) return;
    setSaving(true);
    try {
      const ref = doc(db, "projects", projectId, "finalAccount", "buildup");
      const exists = (await getDoc(ref)).exists();
      const payload = {
        lineItems, totalCertified: data.totalCertified, retentionHeld: data.retentionHeld,
        retentionReleased: data.retentionReleased, status: data.status,
        finalValue, agreedValue, updatedAt: serverTimestamp(),
      };
      if (exists) await updateDoc(ref, payload);
      else await setDoc(ref, { ...payload, createdAt: serverTimestamp() });
      setDirty(false);
    } catch (e) { alert(`Save failed: ${e.message || e}`); }
    finally { setSaving(false); }
  }

  if (!projectId) {
    return <div style={s.page}><div style={s.card}><p style={s.sub}>Select a project to build its final account.</p></div></div>;
  }

  const statusMeta = STATUS_META[data.status] || STATUS_META.Draft;

  return (
    <div style={s.page}>
      <div style={{ ...s.card, display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 14, flexWrap: "wrap" }}>
        <div>
          <h1 style={s.title}>Final Account & Close-Out</h1>
          <p style={s.sub}>Build up the agreed final contract value and reconcile it against what has been certified — with retention release tracked to close-out.</p>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={s.chip(statusMeta)}>{data.status}</span>
          <select style={{ ...s.inp, width: 180 }} value={data.status} onChange={(e) => patch({ status: e.target.value })}>
            {STATUSES.map((x) => <option key={x} value={x}>{x}</option>)}
          </select>
        </div>
      </div>

      {/* ── headline figures ── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 12 }}>
        <div style={s.statTile}><p style={s.statLabel}>Final contract value</p><p style={s.statValue}>{fmtR(finalValue)}</p></div>
        <div style={s.statTile}><p style={s.statLabel}>Agreed ({agreedPct}%)</p><p style={{ ...s.statValue, color: C.success }}>{fmtR(agreedValue)}</p></div>
        <div style={s.statTile}><p style={s.statLabel}>Certified to date</p><p style={s.statValue}>{fmtR(certified)}</p></div>
        <div style={s.statTile}><p style={s.statLabel}>Balance to certify</p><p style={{ ...s.statValue, color: balanceToCertify < 0 ? C.danger : C.ink }}>{fmtR(balanceToCertify)}</p></div>
        <div style={s.statTile}><p style={s.statLabel}>Retention outstanding</p><p style={{ ...s.statValue, color: retentionOutstanding > 0 ? C.warning : C.success }}>{fmtR(retentionOutstanding)}</p></div>
      </div>

      {/* ── build-up ── */}
      <div style={s.card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, flexWrap: "wrap", gap: 8 }}>
          <h3 style={{ ...s.h3, margin: 0 }}>Final statement build-up</h3>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {CATEGORIES.map((cat) => (
              <button key={cat} style={s.btnSmall} onClick={() => addLine(cat)}>+ {cat}</button>
            ))}
          </div>
        </div>

        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr>
              <th style={s.th}>Category</th>
              <th style={s.th}>Description</th>
              <th style={s.thR}>Amount</th>
              <th style={s.th}>Notes</th>
              <th style={s.th}>Agreed</th>
              <th style={s.th}></th>
            </tr></thead>
            <tbody>
              {lineItems.map((li) => (
                <tr key={li.id}>
                  <td style={s.td}>
                    <select style={{ ...s.inp, minWidth: 150 }} value={li.category} onChange={(e) => setLine(li.id, { category: e.target.value })}>
                      {CATEGORIES.map((x) => <option key={x} value={x}>{x}</option>)}
                    </select>
                  </td>
                  <td style={s.td}><input style={{ ...s.inp, minWidth: 180 }} value={li.label} onChange={(e) => setLine(li.id, { label: e.target.value })} placeholder="Description" /></td>
                  <td style={s.tdR}>
                    <input type="number" style={{ ...s.inp, width: 140, textAlign: "right" }} value={li.amount} onChange={(e) => setLine(li.id, { amount: e.target.value })} placeholder="0.00" />
                    {DEDUCTIONS.has(li.category) && <div style={{ ...s.sub, color: C.danger }}>deducted</div>}
                  </td>
                  <td style={s.td}><input style={{ ...s.inp, minWidth: 140 }} value={li.notes} onChange={(e) => setLine(li.id, { notes: e.target.value })} placeholder="—" /></td>
                  <td style={s.td}><input type="checkbox" checked={!!li.agreed} onChange={(e) => setLine(li.id, { agreed: e.target.checked })} /></td>
                  <td style={s.td}><button style={s.btnDanger} onClick={() => removeLine(li.id)}>Remove</button></td>
                </tr>
              ))}
              {!lineItems.length && (
                <tr><td style={{ ...s.td, color: C.inkMuted }} colSpan={6}>No lines yet — start with “+ Original Contract Sum”, then add variations, CPA and dayworks.</td></tr>
              )}
            </tbody>
            <tfoot>
              <tr>
                <td style={{ ...s.td, fontWeight: 800, borderTop: `2px solid ${C.borderStrong || C.border}` }} colSpan={2}>FINAL CONTRACT VALUE</td>
                <td style={{ ...s.tdR, fontWeight: 800, borderTop: `2px solid ${C.borderStrong || C.border}` }}>{fmtR(finalValue)}</td>
                <td style={{ ...s.td, borderTop: `2px solid ${C.borderStrong || C.border}` }} colSpan={3}>{agreedPct}% agreed</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* ── close-out reconciliation ── */}
      <div style={s.card}>
        <h3 style={s.h3}>Close-out reconciliation</h3>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12 }}>
          <div><label style={s.lbl}>Total certified to date (R)</label><input type="number" style={s.inp} value={data.totalCertified} onChange={(e) => patch({ totalCertified: e.target.value })} placeholder="0.00" /></div>
          <div><label style={s.lbl}>Retention held (R)</label><input type="number" style={s.inp} value={data.retentionHeld} onChange={(e) => patch({ retentionHeld: e.target.value })} placeholder="0.00" /></div>
          <div><label style={s.lbl}>Retention released (R)</label><input type="number" style={s.inp} value={data.retentionReleased} onChange={(e) => patch({ retentionReleased: e.target.value })} placeholder="0.00" /></div>
        </div>
        <div style={{ display: "flex", gap: 28, marginTop: 16, flexWrap: "wrap" }}>
          <div><div style={s.lbl}>Balance to certify (final cert)</div><div style={{ ...s.mono, fontWeight: 800, fontSize: 16, color: balanceToCertify < 0 ? C.danger : C.ink }}>{fmtR(balanceToCertify)}</div></div>
          <div><div style={s.lbl}>Retention still to release</div><div style={{ ...s.mono, fontWeight: 800, fontSize: 16, color: retentionOutstanding > 0 ? C.warning : C.success }}>{fmtR(retentionOutstanding)}</div></div>
          <div><div style={s.lbl}>Total still owed at close-out</div><div style={{ ...s.mono, fontWeight: 800, fontSize: 16 }}>{fmtR(balanceToCertify + retentionOutstanding)}</div></div>
        </div>
      </div>

      <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
        <button style={s.btnAccent} onClick={save} disabled={saving}>{saving ? "Saving…" : "Save final account"}</button>
        {dirty && <span style={{ ...s.sub, color: C.warning }}>Unsaved changes</span>}
      </div>
    </div>
  );
}

export default React.memo(FinalAccountSection);
